import React from 'react';
import { Box, Typography, Button, Skeleton } from '@mui/material';
import { commandDashboard } from '../commandDashboardTokens';
import type { CommandHeaderProps } from '../types';

const dotColor: Record<CommandHeaderProps['chips'][number]['dot'], string> = {
  red: commandDashboard.color.dotRed,
  amber: commandDashboard.color.dotAmber,
  green: commandDashboard.color.dotGreen,
  blue: commandDashboard.color.dotBlue,
  slate: commandDashboard.color.dotSlate,
};

export const CommandHeader: React.FC<CommandHeaderProps> = ({
  roleBadge,
  orgName,
  headlineValue,
  headlineLabel,
  subline,
  chips,
  ctaLabel,
  onCtaClick,
  timestamp,
  loading,
}) => (
  <Box
    sx={{
      p: '26px 28px',
      bgcolor: commandDashboard.color.headerGround,
      color: '#fff',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '24px',
      '@media (max-width:700px)': { flexDirection: 'column' },
    }}
  >
    {/* Hero column */}
    <Box sx={{ flex: '1 1 240px', minWidth: 0, '@media (max-width:700px)': { flexBasis: 'auto' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
        <Box
          component="span"
          sx={{
            px: '8px',
            py: '2px',
            borderRadius: `${commandDashboard.radius.tagBadge}px`,
            bgcolor: commandDashboard.color.onNavyBadge,
            color: commandDashboard.color.onNavyBright,
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          {roleBadge}
        </Box>
        <Typography sx={{ fontSize: '12.5px', color: commandDashboard.color.inkFaint }}>{orgName}</Typography>
      </Box>
      {loading ? (
        <Skeleton variant="text" width={140} height={52} sx={{ bgcolor: 'rgba(255,255,255,.12)' }} />
      ) : (
        <Typography
          sx={{
            fontSize: '44px',
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            fontVariantNumeric: 'tabular-nums',
            color: '#fff',
          }}
        >
          {headlineValue}
        </Typography>
      )}
      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: commandDashboard.color.onNavyBright, mt: '4px' }}>
        {headlineLabel}
      </Typography>
      {!loading && (
        <Typography sx={{ fontSize: '12.5px', color: commandDashboard.color.inkFaint, maxWidth: '440px', mt: '4px' }}>
          {subline}
        </Typography>
      )}
    </Box>

    {/* Chips column */}
    <Box sx={{ flex: '1 1 200px', minWidth: 0, pt: '26px', '@media (max-width:700px)': { flexBasis: 'auto', pt: '8px' } }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignContent: 'flex-start' }}>
        {chips.map((chip) => (
          <Box
            key={chip.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              p: '7px 12px',
              borderRadius: `${commandDashboard.radius.chipButton}px`,
              bgcolor: commandDashboard.color.onNavyChip,
              border: `1px solid ${commandDashboard.color.onNavyChipBorder}`,
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dotColor[chip.dot], flex: '0 0 auto' }} />
            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: commandDashboard.color.onNavyBrighter }}>{chip.label}</Typography>
            <Typography sx={{ fontSize: '12.5px', color: commandDashboard.color.inkFaint, fontVariantNumeric: 'tabular-nums' }}>
              {chip.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>

    {/* CTA column */}
    <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', '@media (max-width:700px)': { alignItems: 'stretch', width: '100%' } }}>
      <Button
        onClick={onCtaClick}
        sx={{
          p: '9px 16px',
          border: `1px solid ${commandDashboard.color.onNavyButtonBorder}`,
          bgcolor: commandDashboard.color.onNavyFill,
          color: '#fff',
          borderRadius: `${commandDashboard.radius.chipButton}px`,
          fontSize: '13px',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'background-color 120ms ease',
          '&:hover': { bgcolor: commandDashboard.color.onNavyButtonHover },
        }}
      >
        {ctaLabel}
      </Button>
      <Typography sx={{ fontSize: '11.5px', color: commandDashboard.color.inkFaint, whiteSpace: 'nowrap' }}>
        {timestamp}
      </Typography>
    </Box>
  </Box>
);
